import { LitElement, html, css } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';

import { StockCount } from '../../types/types';
import { getQueryParamURL, getSearchParam } from '../../util/list_util';
import '../../elements/langhout-button/langhout-button';
import {
  authenticatedAPICall,
  isSuccessfulAPIResponse,
} from '../../client/api-client';
import '../../elements/langhout-checkbox/langhout-checkbox';

import {
  StockCountStatusCreated,
  convertStockCountStatusToReadable,
  formatDate,
  formatTimeOnly,
  formatTime,
} from '../../util/util';
import { confirmPopup, showNotification } from '../../LanghoutBetonPortal';
import { closeIcon } from '../../elements/icons/icons';
import { sharedStyles } from '../../elements/shared-styles';

@customElement('langhout-beton-view-stock-count')
export class ViewStockCount extends LitElement {
  static styles = [
    sharedStyles,
    css`
      :host {
        font-size: 17px;
        color: var(--text-color-main);
        height: calc(100vh - 84px);
        max-height: calc(100vh - 84px);
        overflow-y: auto;
        background-color: white;
        z-index: 100;
        padding: 10px;
        padding-left: 30px;
        padding-right: 30px;
      }

      .row {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-grow: 1;
      }

      .centered {
        justify-content: center;
      }

      .inner-wrapper {
        max-width: 900px;
        flex-grow: 1;
      }

      .properties-column-wrapper {
        flex-wrap: wrap;
        flex-flow: row wrap;
      }

      .properties-column {
        width: calc(50%-10px);
        min-width: 250px;
        padding-left: 10px;
      }

      .wrapper {
        padding-bottom: 400px;
        display: flex;
        flex-direction: row;
        justify-content: center;
      }

      .close-wrapper {
        padding: 5px;
      }

      .label {
        text-align: left;
        min-width: 180px;
        max-width: 180px;
        font-weight: bold;
        flex-grow: 1;
      }

      .value {
        text-align: right;
        min-width: 100px;
        flex-grow: 1;
        max-width: 300px;
      }

      langhout-button {
        max-width: 200px;
        min-width: 190px;
        margin-right: 20px;
      }

      :host([disableButtons]) {
        pointer-events: none;
        opacity: 0.7;
      }

      .button-row {
        flex-wrap: wrap;
        flex-flow: row wrap;
      }

      .display-props {
        margin-bottom: 10px;
      }

      @media (max-width: 449px) {
        .hide-fill {
          display: none;
        }
      }

      .comment-wrapper {
        padding: 10px;
        border: 2px solid var(--primary-color);
        margin-top: 15px;
        border-radius: 5px;
      }

      .comment-user-name,
      .comment-time {
        font-weight: bold;
        color: var(--primary-color);
      }

      .comment-add-section {
        margin-top: 30px;
      }

      .adjust-row {
        margin-top: 20px;
      }
    `,
  ];

  @property({ type: Object, reflect: true })
  public currentViewStockCount: StockCount = <StockCount>{};

  private currentID = '';

  @query('#count')
  private countInput?: HTMLInputElement;

  @query('#comment')
  private commentInput?: HTMLInputElement;

  @property({ type: Boolean, reflect: true })
  private disableButtons = false;

  constructor() {
    super();
    this.currentID = getSearchParam(getQueryParamURL(), 'id');
    this.getStockCount();
    document.addEventListener(
      'confirm_stock_count_deletion',
      this.removeStockCountCall,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      'confirm_stock_count_deletion',
      this.removeStockCountCall,
    );
  }

  render() {
    return html`
      <div class="row">
        <div class="fill"></div>
        <div
          class="close-wrapper"
          @click="${() => {
            window.history.back();
          }}"
        >
          ${closeIcon()}
        </div>
      </div>
      <div class="wrapper">
        <div class="inner-wrapper">
          <div class="row centered">
            <h2>Telling</h2>
          </div>
          <div class="row centered">
            <h3>Machine ${this.currentViewStockCount.production_unit_name}</h3>
          </div>
          <div class="row properties-column-wrapper">
            <div class="properties-column">
              <div class="row centered display-props">
                <div class="label">Status:</div>
                <div class="value">
                  ${convertStockCountStatusToReadable(
                    this.currentViewStockCount.status,
                  )}
                </div>
              </div>
              <div class="row centered display-props">
                <div class="label">Product naam:</div>
                <div class="value">
                  ${this.currentViewStockCount.product_name}
                </div>
              </div>
              <div class="row centered display-props">
                <div class="label">Product code:</div>
                <div class="value">
                  ${this.currentViewStockCount.product_code}
                </div>
              </div>
              <div class="row centered display-props"></div>
            </div>
            <div class="fill hide-fill"></div>
            <div class="properties-column">
              <div class="row centered display-props">
                <div class="label">Geteld om:</div>
                <div class="value">
                  ${formatDate(this.currentViewStockCount.time)}
                  ${formatTimeOnly(this.currentViewStockCount.time)}
                </div>
              </div>
              <div class="row centered display-props">
                <div class="label">Geteld door:</div>
                <div class="value">
                  ${this.currentViewStockCount.created_user}
                </div>
              </div>
              <div class="row centered display-props">
                <div class="label">Automatische telling:</div>
                <div class="value">
                  ${
                    this.currentViewStockCount.automatic_stock_count
                      ? html` ${formatDate(
                          this.currentViewStockCount.automatic_stock_count.time,
                        )}
                        ${formatTimeOnly(
                          this.currentViewStockCount.automatic_stock_count.time,
                        )}`
                      : 'Nee'
                  }
                </div>
              </div>
            </div>
          </div>
          <div class="row properties-column-wrapper">
            <div class="properties-column">
              <div class="row centered display-props">
                <div class="label">Aantal (Stuks):</div>
                <div class="value">
                  ${this.renderCount(this.currentViewStockCount)}
                </div>
              </div>
            </div>
          </div>
          ${this.renderButtonRow(this.currentViewStockCount)}
          <h2>Opmerkingen</h2>
          ${this.renderComments(this.currentViewStockCount)}
          <div class="row properties-column-wrapper comment-add-section">
            <div class="properties-column  column">
              <input
                type="text"
                placeholder="Opmerking"
                id="comment"
                name="comment"
                @keypress=${(e: KeyboardEvent) => {
                  if (e.key === 'Enter') {
                    this.addComment();
                  }
                }}
              />
            </div>
            <div class="fill hide-fill"></div>
            <div class="properties-column column centered">
              <langhout-button
                text="Opmerking plaatsen"
                @click="${() => {
                  this.addComment();
                }}"
              ></langhout-button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  renderComments = (stockCount: StockCount) => {
    if (!stockCount.comments) {
      return html` <div class="row centered">Er zijn geen opmerkingen</div> `;
    }
    return html`
      ${this.currentViewStockCount.comments.map(comment => {
        return html`
          <div class="comment-wrapper">
            <div class="row">
              <div class="comment-user-name">${comment.user_name}</div>
              <div class="fill"></div>
              <div class="comment-time">
                ${formatTime(comment.timestamp, false)}
              </div>
            </div>
            <div class="row comment-message">${comment.message}</div>
          </div>
        `;
      })}
    `;
  };

  renderButtonRow = (stockCount: StockCount) => {
    if (stockCount.status !== StockCountStatusCreated) {
      return html``;
    }
    return html`
      <div class="row button-row adjust-row">
        <langhout-button
          text="Wijzigen"
          @click="${async () => {
            this.disableButtons = true;
            await this.updateStockCount();
            this.disableButtons = false;
          }}"
        ></langhout-button>
        <div class="fill"></div>
        <langhout-button
          text="Verwerken in voorraad"
          reverse
          @click="${async () => {
            this.disableButtons = true;
            await this.processStockCount();
            this.disableButtons = false;
          }}"
        ></langhout-button>
        <div class="fill"></div>
        <langhout-button
          text="Verwijderen"
          removeButton
          @click="${() => {
            confirmPopup({
              confirm_event: 'confirm_stock_count_deletion',
              title: 'Verwijderen bevestigen',
              description:
                'Weet u zeker dat u de productie telling wilt verwijderen? Deze actie kan niet ongedaan gemaakt worden!',
              with_checkbox: false,
              checkbox_text: '',
            });
          }}"
        ></langhout-button>
      </div>
    `;
  };

  renderCount = (stockCount: StockCount) => {
    if (stockCount.status === StockCountStatusCreated) {
      return html`
        <input
          type="number"
          id="count"
          name="count"
          .value="${this.currentViewStockCount.count}"
        />
      `;
    }

    return html` ${this.currentViewStockCount.count} `;
  };

  addComment = () => {
    const message = this.commentInput ? this.commentInput.value : '';
    if (message === '') {
      showNotification('Opmerking kan niet leeg zijn');
      return;
    }
    this.addCommentCall(this.currentViewStockCount, message);
  };

  addCommentCall = async (pickupOrder: StockCount, message: string) => {
    const commentPayload = {
      message: message,
      type: 'portal',
    };
    const path = `/stock/count/${pickupOrder.id}/comment`;
    const result = await authenticatedAPICall(
      path,
      'POST',
      new Headers(),
      JSON.stringify(commentPayload),
    );

    if (isSuccessfulAPIResponse(result) && result.body.success) {
      showNotification('Opmerking toegevoegd');
      if (this.commentInput) {
        this.commentInput.value = '';
      }
      this.getStockCount();
    }
  };

  getStockCount = async () => {
    const result = await authenticatedAPICall(
      `/stock/count/${this.currentID}`,
      'GET',
      new Headers(),
    );
    if (isSuccessfulAPIResponse(result)) {
      this.currentViewStockCount = result.body;
    }
  };

  updateStockCount = async () => {
    if (!this.countInput) {
      showNotification('something unexpected went wrong, please try again');
      return;
    }
    const newCount = parseInt(this.countInput.value);
    if (isNaN(newCount) || newCount === 0) {
      showNotification("count can't be empty or zero");
      return;
    }

    const result = await authenticatedAPICall(
      `/stock/count/${this.currentID}`,
      'PUT',
      new Headers(),
      JSON.stringify({ count: newCount }),
    );
    if (isSuccessfulAPIResponse(result)) {
      showNotification('Successfully adjusted stock count');
      this.getStockCount();
    }
  };

  processStockCount = async () => {
    if (!this.countInput) {
      showNotification('something unexpected went wrong, please try again');
      return;
    }

    const result = await authenticatedAPICall(
      `/stock/count/${this.currentID}/process`,
      'POST',
      new Headers(),
    );
    if (isSuccessfulAPIResponse(result)) {
      showNotification('Stock count is being processed');
      window.history.back();
    }
  };

  removeStockCountCall = async () => {
    this.scrollTo(0, 0);
    const result = await authenticatedAPICall(
      `/stock/count/${this.currentID}`,
      'DELETE',
      new Headers(),
    );
    if (isSuccessfulAPIResponse(result) && result.body.success) {
      window.history.back();
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-view-stock-count': ViewStockCount;
  }
}
